import { registerSW } from 'virtual:pwa-register'
import { usePwaStore } from '~~/stores/pwaStore'
import { usePwa } from '~/composables/usePwa'
import { usePwaFeatureFlag } from '~/composables/usePwaFeatureFlag'
import { useAnalytics } from '~/composables/useAnalytics'
import { segmentifyOwnsPushOnBrand } from '~~/shared/constants/segmentifyPush'

export default defineNuxtPlugin(() => {
  // Kill switch / rollout: gdy PWA wyłączona dla tej wizyty, nie rejestrujemy SW
  // ani nie podpinamy listenerów. Bramka obejmuje też SW — spójnie z manifestem
  // i modalem (oba gated w app.vue). Inaczej SW działałby dla 100% ruchu mimo flagi.
  if (!usePwaFeatureFlag().value) {
    return { provide: { updatePwaSw: () => {} } }
  }

  const store = usePwaStore()
  const { detectStandalone, trackVisit, releaseOwnPushForSegmentify } = usePwa()
  // Referencja łapana przy setupie (kontekst Nuxt) — bezpieczna do wołania z listenera.
  const analytics = useAnalytics()
  const segmentifyPush = segmentifyOwnsPushOnBrand(
    useRuntimeConfig().public.brand as string,
  )

  // Detect standalone w pierwszej kolejności
  store.isStandalone = detectStandalone()
  trackVisit()

  // Capture BeforeInstallPrompt (Android Chrome/Edge)
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    store.installPromptDeferred = event as BeforeInstallPromptEvent
    store.canInstall = true
  })

  window.addEventListener('appinstalled', () => {
    store.isStandalone = true
    store.canInstall = false
    store.installPromptDeferred = null
    // Ground-truth instalacji (Android; iOS zwykle nie odpala tego zdarzenia).
    analytics.trackPwaInstalled()
  })

  // Initial push permission state
  if ('Notification' in window) {
    store.pushPermission = Notification.permission as 'default' | 'granted' | 'denied'
  }
  else {
    store.pushPermission = 'unavailable'
  }

  // Rejestracja SW (virtual:pwa-register z @vite-pwa/nuxt) odroczona do
  // onNuxtReady (bezczynność po hydracji) — sam SW nie jest potrzebny do
  // pierwszego renderu, a jego rejestracja + start precache konkurują o wątek
  // główny w oknie CWV. Listenery instalacji/push wyżej zostają synchroniczne
  // (beforeinstallprompt potrafi paść bardzo wcześnie). Referencję updateSW
  // trzymamy w domknięciu, żeby provider działał tak samo po doładowaniu.
  let updateSW: ((reloadPage?: boolean) => Promise<void>) | undefined

  onNuxtReady(() => {
    updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        store.updateAvailable = true
      },
      onRegisteredSW(_swUrl, registration) {
        if (registration) {
          store.swRegistration = registration
          // Przejęcie kanału push przez Segmentify: dopóki w przeglądarce wisi NASZA
          // subskrypcja VAPID, ich `subscribe()` kończy się `InvalidStateError` (jeden
          // scope = jedna subskrypcja, jeden applicationServerKey). Zwalniamy miejsce —
          // ale TYLKO z naszej subskrypcji: ta sama rejestracja trzyma potem subskrypcję
          // Segmentify i ślepe `unsubscribePush()` kasowało ją przy każdym wejściu.
          if (segmentifyPush) {
            releaseOwnPushForSegmentify().catch((err) => {
              console.error('[pwa] releasing own push subscription failed', err)
            })
          }
        }
      },
      onOfflineReady() {
        console.info('[pwa] offline ready')
      },
    })
  })

  // Expose updateSW via Nuxt provider — UpdateAvailableBanner używa.
  // Do czasu rejestracji (przed idle) to no-op — bezpieczne, bo baner
  // aktualizacji i tak pokazuje się dopiero po onNeedRefresh.
  return {
    provide: {
      updatePwaSw: () => updateSW?.(true),
    },
  }
})
